import {
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  Input,
  Layout,
  message,
  Tooltip,
  Typography,
} from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { useForm } from "antd/es/form/Form";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useState } from "react";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import { useTranslation } from "react-i18next";
import PhoneInput, { Country, Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useSearchParams } from "react-router-dom";
import supportAvatar from "../assets/support.jpg";
import { AddressEditor } from "../components/AddressEditor";
import { IntlAddressEditor } from "../components/IntlAddressEditor";
import {
  isValidCNPJ,
  isValidCPF,
  MaskedTaxIdInput,
} from "../components/MaskedTaxIdInput";
import { useAppLanguage } from "../hooks/useAppLanguage";
import { GlobalStore } from "../store/store";
import { formatPrice, getPrice, getProduct, mapEntries } from "../utils";

interface IGoFarmCaptureProps {
  store: GlobalStore;
}

const { Content } = Layout;

interface PhoneFieldProps {
  value?: Value;
  onChange?: (value?: Value) => void;
  placeholder?: string;
  defaultCountry?: Country;
}

const PhoneField: FC<PhoneFieldProps> = ({ value, onChange, ...rest }) => (
  <PhoneInput
    value={value}
    onChange={(v) => onChange?.(v)}
    international
    className="phone-input"
    {...rest}
  />
);

export const GoFarmCapture: FC<IGoFarmCaptureProps> = observer(({ store }) => {
  const { t } = useTranslation();
  const { currency, language } = useAppLanguage();
  const [form] = useForm();
  const [params] = useSearchParams();
  const plan = params.get("plano");

  useEffect(() => {
    if (store.products.length === 0) {
      store.getProducts("gofarm");
    }
  }, []);

  const product = useMemo(() => {
    return getProduct(plan ?? "", store.products)?.[currency ?? ""];
  }, [store.products, plan, currency]);

  const onSubmit = async (values: any) => {
    if (!product) {
      message.warning(t("form.error_loading_product"));
      return;
    }

    let data = mapEntries(values, {
      valueFn: (v: any) => (typeof v === "string" ? v.trim() : v),
    });

    data.tax_id =
      language === "br"
        ? values.tax_id?.replace(/\D/g, "")
        : values.tax_id?.trim();

    const body = {
      ExtProductID: product?.ProductID,
      ExtPriceID: product?.PriceID,
      Data: data,
    };

    const intent_resp = await fetch("/api/create-intent", {
      method: "POST",
      body: JSON.stringify(body),
    });

    const intent = await intent_resp.json();

    const ps = new URLSearchParams();
    ps.set("product_id", product?.ProductID ?? "");
    ps.set("price_id", product?.PriceID ?? "");
    ps.set("intent_id", intent?.ID);

    const checkout_resp = await fetch("/api/create-checkout?" + ps.toString(), {
      method: "POST",
    });

    const loc = await checkout_resp.json();
    if (loc?.URL) {
      window.location.href = loc.URL;
    }
  };

  const products = useMemo(() => {
    return {
      anual: getProduct("anual", store.products),
      trimestral: getProduct("trimestral", store.products),
    };
  }, [store.products]);

  const discount = useMemo(() => {
    const p1 = products["anual"]?.[currency]?.UnitAmount;
    const p2 = products["trimestral"]?.[currency]?.UnitAmount;

    if (!p1 || !p2) {
      return 0;
    }

    return Math.round(((1 - p1 / (p2 * 4)) * 100) as any);
  }, [products, currency]);

  const price_anual = getPrice(products["anual"]?.[currency], currency);

  const [accepted_terms, set_accepted_terms] = useState(false);

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    form.setFieldValue("accepted_terms", e.target.checked);
    form.setFieldValue(
      "accepted_terms_time",
      e.target.checked ? +new Date() : null,
    );
    set_accepted_terms(e.target.checked);
  };

  return (
    <Layout>
      <Content
        style={{ padding: "0 50px 50px 50px", width: 1150, margin: "auto" }}
      >
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>{t("common.products")}</Breadcrumb.Item>
          <Breadcrumb.Item>GoFarm®</Breadcrumb.Item>
          <Breadcrumb.Item>{t("common.register")}</Breadcrumb.Item>
        </Breadcrumb>

        <Card>
          <Typography.Title level={3} style={{ marginTop: 0 }}>
            GoFarm®
          </Typography.Title>

          <div>
            {t("common.product") || "Product"}: {product?.ProductName}
          </div>

          <div>
            {plan === "anual" && (
              <>
                {discount >= 3 ? (
                  <>
                    <p style={{ margin: 0, color: "#aaa" }}>
                      <span style={{ display: "inline-block", width: 44 }}>
                        {t("common.was")}
                      </span>
                      <span style={{ textDecoration: "line-through" }}>
                        {formatPrice(
                          products["trimestral"]?.[currency]?.UnitAmount! * 4,
                          currency,
                        )}
                      </span>
                    </p>
                    <p style={{ margin: 0 }}>
                      <span style={{ display: "inline-block", width: 44 }}>
                        {t("common.now")}
                      </span>
                      <span>{price_anual}</span>
                    </p>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0 }}>{price_anual}</p>
                  </>
                )}
              </>
            )}

            {plan !== "anual" && (
              <>
                {t("common.price") || "Price"}: {getPrice(product, currency)}
              </>
            )}
          </div>

          <Divider />

          <Form layout="vertical" form={form} onFinish={onSubmit}>
            <Typography.Title level={5}>
              {t("form.contact_details")}
            </Typography.Title>

            <Form.Item
              required
              validateTrigger="onBlur"
              rules={[
                { required: true, message: t("form.full_name_required") },
              ]}
              name="nome_completo"
              label={t("form.full_name")}
            >
              <Input />
            </Form.Item>

            <Form.Item
              required
              validateTrigger="onBlur"
              rules={[{ required: true, message: t("form.phone_required") }]}
              name="telefone"
              label={t("form.phone")}
            >
              <PhoneField
                placeholder={t("form.phone_placeholder")}
                defaultCountry={language === "br" ? "BR" : undefined}
              />
            </Form.Item>

            <Form.Item
              required
              validateTrigger="onBlur"
              rules={[
                { required: true, message: t("form.email_required") },
                { type: "email", message: t("form.email_invalid") },
              ]}
              name="email"
              label={t("form.email")}
            >
              <Input />
            </Form.Item>

            <Typography.Title level={5}>
              {t("form.billing_details")}
            </Typography.Title>

            <Form.Item
              required
              validateTrigger="onBlur"
              rules={[
                { required: true, message: t("form.business_name_required") },
              ]}
              name="razao_social"
              label={t("form.business_name")}
            >
              <Input />
            </Form.Item>

            {language === "br" && (
              <Form.Item
                label={t("form.state_registration")}
                name="inscricao_estadual"
              >
                <Input />
              </Form.Item>
            )}

            <Form.Item
              required
              validateTrigger="onBlur"
              rules={[
                { required: true, message: t("form.tax_id_required") },
                ...(language !== "br"
                  ? [
                      {
                        pattern: /^[A-Z0-9][A-Z0-9 -]{4,18}[A-Z0-9]$/,
                        message: t("form.tax_id_invalid"),
                      },
                    ]
                  : [
                      {
                        validator: (_: any, value: any) => {
                          if (!value) return Promise.resolve();
                          const digits = value.replace(/\D/g, "");
                          if (digits.length <= 11) {
                            return isValidCPF(digits)
                              ? Promise.resolve()
                              : Promise.reject(new Error("CPF inválido"));
                          }
                          return isValidCNPJ(digits)
                            ? Promise.resolve()
                            : Promise.reject(new Error("CNPJ inválido"));
                        },
                      },
                    ]),
              ]}
              name="tax_id"
              label={t("form.tax_id")}
              normalize={
                language !== "br" ? (v) => v?.toUpperCase() : undefined
              }
            >
              {language === "br" ? (
                <MaskedTaxIdInput placeholder="000.000.000-00 ou 00.000.000/0000-00" />
              ) : (
                <Input
                  placeholder={t("form.tax_id_placeholder") || "Enter Tax ID"}
                />
              )}
            </Form.Item>

            <div style={{ marginBottom: 10, fontWeight: "bold" }}>
              {t("form.address")}
            </div>

            {language === "br" ? (
              <AddressEditor form={form} baseName="endereco" />
            ) : (
              <IntlAddressEditor form={form} baseName="endereco" />
            )}

            <Form.Item required name="accepted_terms">
              <Checkbox onChange={handleCheckboxChange}>
                {t("form.terms_label")}{" "}
                <a
                  href={`${process.env.PUBLIC_URL}${t("form.terms_pdf_link")}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("form.terms_link")}
                </a>
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Tooltip
                title={accepted_terms ? "" : t("form.terms_accept_required")}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!accepted_terms}
                  style={{ width: 140 }}
                >
                  {t("form.register_button")}
                </Button>
              </Tooltip>
            </Form.Item>
          </Form>
        </Card>

        <FloatingWhatsApp
          accountName="Suporte Tecgraf Agro"
          phoneNumber="+5519999174690"
          avatar={supportAvatar}
          statusMessage={t("common.support_status")}
          chatMessage={t("common.support_message")}
          placeholder={t("common.whatsapp_placeholder")}
        />
      </Content>
    </Layout>
  );
});
